import { lazy, Suspense, useEffect } from "react";
import "./App.css";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { Navigate } from "react-router-dom";
import type { Location } from "react-router-dom";
import { Landing } from "./pages/landing.tsx";
import { Loader } from "./pages/loader.tsx";
import { ProviderInfo } from "./pages/ProviderInfo";
import { ProviderPreview } from "./pages/ProviderPreview";
import { ProveedoresGeneral } from "./pages/ProveedoresGeneral.tsx";
import { ServiceProfile } from "./pages/ServiceProfile";
import { PoliticasPage } from "./pages/politicas.tsx";
import { TerminosCondicionesPage } from "./pages/TerminosCondiciones.tsx";
import { PoliticasCookiesPage } from "./pages/PoliticasCookies.tsx";
import { AcercaPage } from "./pages/acerca.tsx";
import { FaqPage } from "./pages/faq.tsx";
import { ContactoPage } from "./pages/contacto.tsx";
import { UserProfile } from "./pages/UserProfile";
import { Profile } from "./pages/Profile";
import { ElaboracionCotizacion } from "./pages/elaboracionCotizacion.tsx";
import { ClientInfoPedido } from "./pages/ClientInfoPedido.tsx";
import { Registro } from "./pages/Registration";
import { Login } from "./pages/login.tsx";
import { AdminLogin } from "./pages/AdminLogin.tsx";
import { RegistrationClient } from "./pages/RegistrationClient";
import { RegistrationProvider } from "./pages/RegistrationProvider";
import { Error404Page } from "./pages/Error404.tsx";
import { Error500Page } from "./pages/Error500.tsx";
import { ChatProvider } from "./state/ChatContext.tsx";
import { FloatingChatToggle } from "./components/chat/FloatingChatToggle.tsx";
import { LayoutChat } from "./layout/ChatLayout.tsx";
import { ServiceRegistrationWizardProvider } from "./state/ServiceRegistrationWizardContext.tsx";
import { NotificationsProvider } from "./state/NotificationsContext.tsx";
import { AuthSessionProvider } from "./components/routing/AuthSessionContext.tsx";
import { PendingReviewProvider } from "./state/PendingReviewContext.tsx";
import { useAuthSessionContext } from "./components/routing/auth-session-store";
import { ProtectedRoute } from "./components/routing/ProtectedRoute.tsx";
import { PublicOnlyRoute } from "./components/routing/PublicOnlyRoute.tsx";
import { ManualManager } from "./pages/ManualManager.tsx";
import ResetPassword from "./pages/ResetPassword.tsx";
import ForgotPassword from "./pages/ForgotPassword.tsx";
import { VerifyEmail } from "./pages/VerifyEmail.tsx";
import { ManualCliente } from "./pages/Manuales.tsx";
import { AppErrorBoundary } from "./components/routing/AppErrorBoundary.tsx";

const GERENTE_ROLES = ["Gerente"] as const;
const GERENTE_SOPORTE_ROLES = ["Gerente", "Soporte"] as const;
const PROVEEDOR_ROLES = ["Proveedor"] as const;
const CLIENTE_ROLES = ["Cliente"] as const;
const CLIENTE_PROVEEDOR_ROLES = ["Cliente", "Proveedor"] as const;

const AdminProveedor = lazy(() => import("./pages/AdminProveedor.tsx"));
const AdminDashboard = lazy(() =>
	import("./pages/AdminDashboard.tsx").then((module) => ({
		default: module.AdminDashboard,
	})),
);
const AdminPedidos = lazy(() =>
	import("./pages/AdminPedidos.tsx").then((module) => ({
		default: module.AdminPedidos,
	})),
);
const AdminArbitraje = lazy(() =>
	import("./pages/AdminArbitraje.tsx").then((module) => ({
		default: module.AdminArbitraje,
	})),
);
const AdminConciliacionPagos = lazy(() =>
	import("./pages/AdminConciliacionPagos.tsx").then((module) => ({
		default: module.AdminConciliacionPagos,
	})),
);
const AdminResenas = lazy(() =>
	import("./pages/AdminResenas.tsx").then((module) => ({
		default: module.AdminResenas,
	})),
);
const AdminSystemConfig = lazy(() =>
	import("./pages/AdminSystemConfig.tsx").then((module) => ({
		default: module.AdminSystemConfig,
	})),
);
const AdminInfoPedido = lazy(() =>
	import("./pages/AdminInfoPedidos.tsx").then((module) => ({
		default: module.AdminInfoPedido,
	})),
);
const AdminInfoProveedor = lazy(() =>
	import("./pages/AdminInfoProveedor.tsx").then((module) => ({
		default: module.AdminInfoProveedor,
	})),
);
const AdminClientes = lazy(() =>
	import("./pages/AdminClientes.tsx").then((module) => ({
		default: module.AdminClientes,
	})),
);
const AdminPersonalAdministrativo = lazy(() =>
	import("./pages/AdminPersonalAdministrativo.tsx").then((module) => ({
		default: module.AdminPersonalAdministrativo,
	})),
);
const AdminCupones = lazy(() =>
	import("./pages/AdminCupones.tsx").then((module) => ({
		default: module.AdminCupones,
	})),
);
const ClientDetails = lazy(() =>
	import("./components/admin-views/gerencia/ClientDetails.tsx").then(
		(module) => ({ default: module.ClientDetails }),
	),
);
const ChatExpanded = lazy(() =>
	import("./pages/ChatExpanded.tsx").then((module) => ({
		default: module.ChatExpanded,
	})),
);
const ServiceRequestWizardPage = lazy(() =>
	import("./pages/ServiceRequestWizard.tsx").then((module) => ({
		default: module.ServiceRequestWizardPage,
	})),
);
const RegistrationWizard = lazy(() =>
	import("./pages/RegistrationWizard.tsx").then((module) => ({
		default: module.RegistrationWizard,
	})),
);

function ModalOverlay({ children }: { children: React.ReactNode }) {
	/* Lock body scroll while the overlay is mounted */
	useEffect(() => {
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev;
		};
	}, []);

	return (
		<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-6'>
			<div className='relative w-full max-w-6xl overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-2xl'>
				<div className='max-h-[92vh] overflow-y-auto overscroll-contain'>
					{children}
				</div>
			</div>
		</div>
	);
}

function ScopedRouteBoundary({
	scope,
	children,
}: {
	scope: "admin" | "chat" | "wizard";
	children: React.ReactNode;
}) {
	return (
		<AppErrorBoundary scope={scope}>
			<Suspense fallback={<Loader />}>
				{children}
			</Suspense>
		</AppErrorBoundary>
	);
}

function LandingRouteGuard() {
	const { session } = useAuthSessionContext();
	const role = session?.user?.type_user ?? null;

	if (role === "Gerente") {
		return <Navigate to='/admin/dashboard' replace />;
	}

	if (role === "Soporte") {
		return <Navigate to='/admin/dashboard' replace />;
	}

	return <Landing />;
}

function AppRoutes() {
	const location = useLocation();
	const { session } = useAuthSessionContext();
	const state = location.state as
		| { backgroundLocation?: Location; modal?: boolean }
		| undefined;
	const backgroundLocation = state?.backgroundLocation;
	{
		/* Render routes with potential background location for modals */
	}
	return (
		<>
			<Routes location={backgroundLocation || location}>
				{/* Public routes */}
				<Route path='/' element={<LandingRouteGuard />} />
				<Route
					path='/login'
					element={
						<PublicOnlyRoute>
							<Login mode='general' />
						</PublicOnlyRoute>
					}
				/>
				<Route
					path='/_internal/admin-access'
					element={
						<PublicOnlyRoute>
							<AdminLogin />
						</PublicOnlyRoute>
					}
				/>
				<Route
					path='/registro'
					element={
						<PublicOnlyRoute>
							<Registro />
						</PublicOnlyRoute>
					}
				/>
				<Route path='/register' element={<Navigate to='/registro' replace />} />
				<Route path='/signup' element={<Navigate to='/registro' replace />} />
				<Route path='/about' element={<Navigate to='/acerca' replace />} />
				<Route
					path='/terms'
					element={<Navigate to='/terminos-condiciones' replace />}
				/>
				<Route path='/privacy' element={<Navigate to='/politicas' replace />} />
				<Route path='/home' element={<Navigate to='/' replace />} />
				<Route path='/services' element={<Error404Page />} />
				<Route path='/admin' element={<Error404Page />} />
				<Route path='/dashboard' element={<Error404Page />} />
				<Route path='/loader' element={<Loader />} />

				{/* Public professional browsing */}
				<Route path='/proveedor/:id' element={<ProviderInfo />} />
				<Route path='/proveedor-preview/:id' element={<ProviderPreview />} />
				<Route
					path='/registro/cliente'
					element={
						<PublicOnlyRoute>
							<RegistrationClient />
						</PublicOnlyRoute>
					}
				/>
				<Route
					path='/registro/proveedor'
					element={
						<PublicOnlyRoute>
							<RegistrationProvider />
						</PublicOnlyRoute>
					}
				/>
				{/* Rutas de autenticación accesibles sin login */}
				<Route path='/forgot-password' element={<ForgotPassword />} />
				<Route path='/recuperar-clave' element={<ForgotPassword />} />
				<Route path='/reset-password' element={<ResetPassword />} />
				<Route path='/verify-email' element={<VerifyEmail />} />

				{/* Protected routes */}
				<Route element={<ProtectedRoute />}>
					{/* Authenticated base routes */}
					<Route path='/profile' element={<Profile />} />
					<Route path='/user-profile' element={<UserProfile />} />
					<Route
						element={
							<ProtectedRoute allowedRoles={CLIENTE_PROVEEDOR_ROLES} />
						}
					>
						<Route
							path='/chat-expanded'
							element={
								<ScopedRouteBoundary scope='chat'>
									<LayoutChat>
										<ChatExpanded
											isProviderView={
												session?.user.type_user === "Proveedor"
											}
											userId={session?.user.id}
										/>
									</LayoutChat>
								</ScopedRouteBoundary>
							}
						/>
					</Route>

					{/* Gerente + Soporte routes */}
					<Route element={<ProtectedRoute allowedRoles={GERENTE_SOPORTE_ROLES} />}>
						<Route path='/admin/dashboard' element={<ScopedRouteBoundary scope='admin'><AdminDashboard /></ScopedRouteBoundary>} />
						<Route path='/admin/pedidos' element={<ScopedRouteBoundary scope='admin'><AdminPedidos /></ScopedRouteBoundary>} />
						<Route
							path='/admin/info/pedido/:id'
							element={<ScopedRouteBoundary scope='admin'><AdminInfoPedido /></ScopedRouteBoundary>}
						/>
						<Route path='/admin/proveedor' element={<ScopedRouteBoundary scope='admin'><AdminProveedor /></ScopedRouteBoundary>} />
						<Route
							path='/admin/info/proveedor'
							element={<ScopedRouteBoundary scope='admin'><AdminInfoProveedor /></ScopedRouteBoundary>}
						/>
						<Route
							path='/admin/info/proveedor/:id'
							element={<ScopedRouteBoundary scope='admin'><AdminInfoProveedor /></ScopedRouteBoundary>}
						/>
						<Route path='/admin/arbitraje' element={<ScopedRouteBoundary scope='admin'><AdminArbitraje /></ScopedRouteBoundary>} />
						<Route
							path='/admin/conciliacion-pagos'
							element={<ScopedRouteBoundary scope='admin'><AdminConciliacionPagos /></ScopedRouteBoundary>}
						/>
					</Route>

					{/* Gerente-only routes */}
					<Route element={<ProtectedRoute allowedRoles={GERENTE_ROLES} />}>
						<Route path='/admin/clientes' element={<ScopedRouteBoundary scope='admin'><AdminClientes /></ScopedRouteBoundary>} />
						<Route
							path='/clients/client/:clientId'
							element={<ScopedRouteBoundary scope='admin'><ClientDetails /></ScopedRouteBoundary>}
						/>
						<Route
							path='/admin/personal-administrativo'
							element={<ScopedRouteBoundary scope='admin'><AdminPersonalAdministrativo /></ScopedRouteBoundary>}
						/>
						<Route path='/admin/cupones' element={<ScopedRouteBoundary scope='admin'><AdminCupones /></ScopedRouteBoundary>} />
						<Route path='/admin/resenas' element={<ScopedRouteBoundary scope='admin'><AdminResenas /></ScopedRouteBoundary>} />
						<Route
							path='/admin/configuracion'
							element={<ScopedRouteBoundary scope='admin'><AdminSystemConfig /></ScopedRouteBoundary>}
						/>
						<Route path='/admin/manual' element={<ManualManager />} />
					</Route>

					{/* Proveedor routes */}
					<Route element={<ProtectedRoute allowedRoles={PROVEEDOR_ROLES} />}>
						<Route path='/service/profile' element={<ServiceProfile />} />
						<Route
							path='/elaboracion-cotizacion'
							element={<ElaboracionCotizacion />}
						/>
					</Route>

					{/* Cliente routes */}
					<Route element={<ProtectedRoute allowedRoles={CLIENTE_ROLES} />}>
						<Route
							path='/solicitud-servicio-wizard'
							element={<ScopedRouteBoundary scope='wizard'><ServiceRequestWizardPage /></ScopedRouteBoundary>}
						/>
						<Route
							path='/registro/servicio'
							element={<ScopedRouteBoundary scope='wizard'><RegistrationWizard /></ScopedRouteBoundary>}
						/>
					</Route>

					{/* Shared Cliente/Proveedor routes */}
					<Route
						element={
							<ProtectedRoute allowedRoles={CLIENTE_PROVEEDOR_ROLES} />
						}
					>
						<Route path='/info/pedido/:id' element={<ClientInfoPedido />} />
					</Route>
				</Route>

				<Route path='/proveedores/general' element={<ProveedoresGeneral />} />

				{/* Public informational routes */}
				<Route
					path='/terminos-condiciones'
					element={<TerminosCondicionesPage />}
				/>
				<Route path='/politicas-cookies' element={<PoliticasCookiesPage />} />
				<Route path='/acerca' element={<AcercaPage />} />
				<Route path='/faq' element={<FaqPage />} />
				<Route path='/contacto' element={<ContactoPage />} />
				<Route path='/politicas' element={<PoliticasPage />} />
				<Route path='/manuales' element={<ManualCliente />} />

				{/* Error routes */}
				<Route path='/error/500' element={<Error500Page />} />
				<Route path='*' element={<Error404Page />} />
			</Routes>
			{state?.modal && backgroundLocation && (
				<Routes>
					<Route
						path='/solicitud-servicio-wizard'
						element={
							<ProtectedRoute allowedRoles={CLIENTE_ROLES}>
								<ScopedRouteBoundary scope='wizard'>
									<ModalOverlay>
										<ServiceRequestWizardPage />
									</ModalOverlay>
								</ScopedRouteBoundary>
							</ProtectedRoute>
						}
					/>
				</Routes>
			)}
		</>
	);
}

function App() {
	return (
		<Router>
			<ServiceRegistrationWizardProvider>
				<NotificationsProvider>
					<AuthSessionProvider>
						<PendingReviewProvider>
							<ChatProvider>
								<AppRoutes />
								<FloatingChatToggle />
							</ChatProvider>
						</PendingReviewProvider>
					</AuthSessionProvider>
				</NotificationsProvider>
			</ServiceRegistrationWizardProvider>
		</Router>
	);
}

export default App;
